/**
 * Permits — the permit capacity and sign-up links for this tenant's account.
 *
 * The demo leads with the operational answer: how full each permit bucket is,
 * what access window applies, and where the next person registers. Detailed
 * people management remains on Parkers, so this surface does not duplicate
 * the roster or make managers reconcile two versions of the same list.
 *
 * ─── IT LEADS WITH THE ACCESS WINDOW, NOT A HEADCOUNT ────────────────────────
 * Deliberate, and the reason is enforcement rather than layout. The window is
 * the only place a permit's actual terms appear, and it is the same schedule
 * the enforcement lookup evaluates: a permit ending Dec 31 means a car there on
 * Jan 1 is not authorized, which is what turns into "why was my employee
 * booted". A count can't be argued with; a date can be checked.
 *
 * ─── IT SHOWS A SCHEDULE WHEN ONE IS REAL, AND SAYS 24/7 WHEN NONE IS ────────
 * This is PR 4 of docs/PERMIT-ACCESS-HOURS-SPEC.md §17. A permit can carry a
 * weekly schedule (`Permit.accessHours`) and enforcement honours it — so a
 * manager whose staff are genuinely bound to weekday hours could previously
 * read the opposite on this screen, in their operator's own product.
 *
 * Every string here is the server's `formatAccessHours` output, the SAME
 * rendering the patrol row and support appeals use. The client formats nothing.
 *
 * ⚠️ DISPLAY ONLY, AND THAT IS A HARD LINE, NOT A PHASE. Nothing a tenant
 * manager can reach writes this field, and §12 refuses giving them one "in any
 * form, including 'phase 1 of' the real thing": hours in something that looks
 * like a control, with no control reading it, tells a manager they limited
 * their contractor to business hours when they did not — and they find out from
 * a 3am incident. The server sends a pre-rendered SENTENCE precisely so there
 * is no structure here to bind an input to.
 *
 * ─── READ-ONLY ON PURPOSE ────────────────────────────────────────────────────
 * No revoke button. A tenant grants parking by APPROVING a registration, which
 * re-checks the seat cap server-side; taking it away is an operator action with
 * an enforcement consequence (a plate that stops being authorized mid-day), and
 * this PR is not the place to invent that flow. Managers who need one ask their
 * operator — the same path that created these permits.
 */
import { useEffect, useMemo, useState } from 'react'
import type { TenantSession } from '../lib/session'
import { listTenantPermits, listRegistrations, listProgramAllocations, type RosterPermit, type TenantPermitBilling, type ProgramAllocation } from '../lib/api'
import { typeLabel, DEFAULT_PERMIT_TYPE, capacityLabel, pendingLabel, type TypePressure } from '../lib/permitTypes'
import { TypeChip } from '../components/TypeChip'
import { countRosterParkers } from '../lib/welcome'
import { labelStyle, purpleBtn, ghostBtn } from '../ui'


const fmtDate = (iso?: string) => {
  if (!iso) return '—'
  try {
    // Parsed as a LOCAL date, not UTC: `new Date('2026-12-31')` is midnight UTC,
    // which renders as Dec 30 anywhere west of Greenwich — an off-by-one on the
    // exact field this screen exists to show.
    const [y, m, d] = iso.slice(0, 10).split('-').map(Number)
    if (!y || !m || !d) return iso
    return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  } catch { return iso }
}

/** The access window, which is the headline of every card. */
function windowLabel(p: RosterPermit): string {
  // `''` is a real, valid state — an open-ended permit — and must read as such
  // rather than as a missing end date.
  if (!p.validUntil) return `From ${fmtDate(p.validFrom)} · no end date`
  return `${fmtDate(p.validFrom)} – ${fmtDate(p.validUntil)}`
}

const STATUS_TONE: Record<RosterPermit['status'], { bg: string; fg: string; border: string; label: string }> = {
  active: { bg: 'rgba(21,128,61,0.10)', fg: '#15803d', border: 'rgba(21,128,61,0.30)', label: 'Active' },
  expiringSoon: { bg: 'rgba(245,158,11,0.12)', fg: '#92400e', border: 'rgba(245,158,11,0.35)', label: 'Ending soon' },
  expired: { bg: 'var(--color-gray-100)', fg: 'var(--color-gray-600)', border: 'var(--color-gray-200)', label: 'Not active' },
}

export function Permits({ session, hideMonetaryValues = false, onViewParkers, onSubtitleChange }: {
  session: TenantSession
  hideMonetaryValues?: boolean
  /** Updates the shared page header once the server-backed permit model is known. */
  onSubtitleChange?: (subtitle: string | null) => void
  /** Jumps to the Parkers tab with that permit type pre-selected, and with
   *  the one permit too when the card was a named permit. */
  onViewParkers?: (permitType: string, permitId?: string) => void
}) {
  const [rows, setRows] = useState<RosterPermit[]>([])
  // Named permits (operator setting): one card per permit, titled by its name.
  const [byName, setByName] = useState(false)
  const [loading, setLoading] = useState(true)
  // Distinct from `rows.length === 0`. A failed read must never render as an
  // empty roster — see the api.ts note.
  const [err, setErr] = useState('')
  const [typeFilter, setTypeFilter] = useState<string | null>(null)
  // Absent = the operator configured no per-type sub-caps, so there are no
  // capacity cards to draw. Not an error, and not zero — same rule as Parkers.
  const [seatTypes, setSeatTypes] = useState<{
    caps: Record<string, number>
    usedByType: Record<string, number>
    pressure?: Record<string, TypePressure>
  } | null>(null)
  const [billing, setBilling] = useState<TenantPermitBilling | null>(null)
  // null = not loaded or the read failed; [] = the operator has allocated no shared-program seats to this tenant.
  const [programs, setPrograms] = useState<ProgramAllocation[] | null>(null)

  const load = async () => {
    setLoading(true); setErr('')
    try {
      const res = await listTenantPermits(session.token)
      setRows(res.permits)
      setByName(res.permitsByName === true)
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'load failed'); console.error('[tenant] permits', e)
    } finally { setLoading(false) }
    // Capacity is a SEPARATE read with its own try/catch: it's context, and a
    // failed capacity call must not blank out the permit list that is this
    // screen's actual job. Missing cards read as "no sub-caps configured",
    // which is a real state — so this failure is logged, not surfaced.
    try {
      const res = await listRegistrations(session.token)
      setSeatTypes(res.seatTypes ?? null)
      setBilling(hideMonetaryValues ? null : (res.billing ?? null))
      // Austin's header describes the tenant's arrangement. Use only the
      // model and allowance the live API actually returns: cadence and prices
      // belong to the operator agreement and are not safe to manufacture here.
      const subtitle = hideMonetaryValues
        ? 'Employee parking for your team'
        : res.billing?.model === 'included'
        ? 'Employee parking included in your lease'
        : res.billing?.model === 'overage'
          ? `${res.billing.includedPermitCount} spaces included · additional at market rate`
          : res.billing?.model === 'purchased'
            ? 'Permits purchased'
            : null
      onSubtitleChange?.(subtitle)
    } catch (e) {
      console.error('[tenant] permit capacity', e)
      setBilling(null)
      onSubtitleChange?.(null)
    }
    // Shared programs are a third, independent read. A failure here hides the
    // section rather than the permit list, and never renders as "no programs".
    try {
      const res = await listProgramAllocations(session.token)
      setPrograms(res.programs)
    } catch (e) {
      console.error('[tenant] program allocations', e)
      setPrograms(null)
    }
  }

  useEffect(() => {
    if (session.kind === 'device') return   // a tablet 403s here; App.tsx never routes one
    void load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * Permits per type, for the filter chips.
   *
   * Counts PERMIT RECORDS, which is the same unit `seatTypes.usedByType`
   * counts (a permit is a seat) — so unlike the Parkers roster, the chips here
   * and the capacity cards above them agree by construction. Kept as its own
   * count anyway rather than reading the seat map, because the chips must
   * describe the list they filter: the seat map counts only LIVE permits,
   * while this list also shows ended ones.
   */
  const typeCounts = useMemo(() => {
    const out = new Map<string, number>()
    for (const p of rows) {
      const t = p.permitType || DEFAULT_PERMIT_TYPE
      out.set(t, (out.get(t) ?? 0) + 1)
    }
    return out
  }, [rows])

  if (session.kind === 'device') return null

  // Capacity cards are the type filter on this screen when they exist; the
  // chips below stand in when they don't. See the render note.
  const hasCapacityCards = !!seatTypes && Object.keys(seatTypes.caps).length > 0

  const visible = typeFilter
    ? rows.filter(p => (p.permitType || DEFAULT_PERMIT_TYPE) === typeFilter)
    : rows
  const live = visible.filter(p => p.status !== 'expired')
  const past = visible.filter(p => p.status === 'expired')
  const livePrograms = groupPermitPrograms(live, { byName })
  const pastPrograms = groupPermitPrograms(past, { byName })

  return (
    <div>
      {/* ── Capacity by permit type ───────────────────────────────────────────
          Capacity AND the sign-up link that fills each bucket, on ONE screen.
          These used to be split — capacity rows plus links on Parkers, capacity
          cards here — on the theory that each screen answered a different
          question. In practice it put the same used/cap numbers on two tabs and
          made a manager hunt for the link, so Parkers' block is gone and this is
          the one place both live. Matches the demo, whose Parkers has no
          capacity block at all.

          The card is therefore doing three jobs at once, in the order a manager
          needs them: how full is this bucket, what is queued against it, and
          where do I send the next person. */}
      {/* ── Shared programs ─────────────────────────────────────────────────
          Fixed seats the operator allocated to this tenant inside a shared
          program. Registering through a program link lands the request in this
          allocation, and approving it takes one of these seats — the server
          reserves it; this card only reports. */}
      {programs && programs.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 18 }}>
          {programs.map(program => (
            <ProgramAllocationCard key={program.id} program={program} tenantId={session.tenantId} />
          ))}
        </div>
      )}
      {hasCapacityCards && seatTypes && (
        <div style={{ marginTop: 0 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {Object.entries(seatTypes.caps).map(([type, cap]) => (
              <CapacityCard
                key={type}
                type={type}
                cap={cap}
                used={seatTypes.usedByType[type] ?? 0}
                pressure={seatTypes.pressure?.[type]}
                accessHours={rows.find(p => (p.permitType || DEFAULT_PERMIT_TYPE) === type)?.accessHours}
                tenantId={session.tenantId}
                active={typeFilter === type}
                onToggle={() => setTypeFilter(typeFilter === type ? null : type)}
                onViewParkers={onViewParkers && (() => onViewParkers(type))}
              />
            ))}
          </div>
          {billing && (
            <div style={{ marginTop: 14, fontSize: 12.5, fontWeight: 600, lineHeight: 1.55, color: 'var(--color-gray-600)' }}>
              Send staff the sign-up link for the permit they should hold. Anyone who registers through it lands in your approval queue already tagged with that type, so approving confirms they work here.{' '}
              {billing.model === 'included'
                ? 'These permits are part of your lease — nothing is billed for staff parking.'
                : billing.model === 'overage'
                  ? `Your lease includes ${billing.includedPermitCount} ${billing.includedPermitCount === 1 ? 'space' : 'spaces'} at no charge. Additional permits are billed under your agreement with your parking operator.`
                  : 'Staff parking is billed under your agreement with your parking operator.'}
            </div>
          )}
        </div>
      )}

      {err && (
        <div style={{ background: 'rgba(237,28,36,0.10)', border: '1.5px solid rgba(237,28,36,0.4)', borderRadius: 12, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', marginTop: 16 }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: '#b91c1c', marginBottom: 2 }}>Couldn't load your permits</div>
            {/* The server's own message is NOT echoed here: this endpoint has
                exactly one failure mode (the read didn't answer), so its text
                only ever restates the heading. The detail is on the console for
                whoever is debugging. What a manager needs instead is the thing
                the error does not say — that nobody's parking changed. */}
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-gray-700)', lineHeight: 1.5 }}>
              This is a loading problem, not a change to anyone's parking — every permit is still exactly as it was.
            </div>
          </div>
          <button onClick={() => void load()} style={purpleBtn}>Retry</button>
        </div>
      )}

      {!err && !hasCapacityCards && (
        <>
          {/* The FALLBACK type filter, for a tenant whose operator set no
              sub-caps: with no caps there are no capacity cards, and the cards
              are what filter this screen. Chips and cards are never both shown
              — they'd be two controls doing one job, and worse, the chips count
              permit RECORDS in this list (ended ones included) while the cards
              count LIVE SEATS, so sitting them adjacent would put two different
              numbers for "Staff" side by side and leave the manager to work out
              why. */}
          {!hasCapacityCards && (typeCounts.size > 1 || typeFilter !== null) && (
            <div style={{ display: 'flex', gap: 6, marginTop: 20, flexWrap: 'wrap' }}>
              <TypeChip label="All" count={rows.length} active={typeFilter === null} onClick={() => setTypeFilter(null)} />
              {[...typeCounts.entries()]
                .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
                .map(([type, count]) => (
                  <TypeChip
                    key={type} label={typeLabel(type)} count={count}
                    active={typeFilter === type} onClick={() => setTypeFilter(typeFilter === type ? null : type)}
                  />
                ))}
            </div>
          )}

          <div style={{ marginTop: 20 }}>
            <div style={{ ...labelStyle, marginBottom: 8 }}>Current</div>
            {livePrograms.length === 0 ? (
              <div style={{ background: '#fff', border: '1px solid var(--color-gray-200)', borderRadius: 12, padding: 28, textAlign: 'center', fontSize: 13, fontWeight: 600, color: 'var(--color-gray-600)' }}>
                {loading
                  ? ''
                  : typeFilter
                    ? `No active ${typeLabel(typeFilter)} permits.`
                    : 'Nobody has a permit yet. Approved staff registrations show up here.'}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {livePrograms.map(program => (
                  <PermitProgramCard
                    key={program.permitId ?? program.type}
                    program={program}
                    tenantId={session.tenantId}
                    onViewParkers={onViewParkers && (() => onViewParkers(program.type, program.permitId))}
                  />
                ))}
              </div>
            )}
          </div>

          {pastPrograms.length > 0 && (
            <div style={{ marginTop: 26 }}>
              <div style={{ ...labelStyle, marginBottom: 8 }}>Ended</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {pastPrograms.map(program => (
                  <PermitProgramCard
                    key={program.permitId ?? program.type}
                    program={program}
                    tenantId={session.tenantId}
                    onViewParkers={onViewParkers && (() => onViewParkers(program.type, program.permitId))}
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

/**
 * How full one permit type is — and the two things a manager does next with
 * that: narrow this screen's list to that bucket, or go see the actual people
 * in it. NO dollar figure, ever: the demo prices overage at a market rate, but
 * real rates are per-zone rate products and inventing one here would put a
 * fabricated number in front of a billing decision (see the same reasoning in
 * api/src/tenants/permit_billing.ts). Counts only.
 */
function CapacityCard({ type, cap, used, pressure, accessHours, tenantId, active, onToggle, onViewParkers }: {
  type: string
  cap: number
  used: number
  /** The queue against this bucket. Absent = nothing proven, so nothing said. */
  pressure?: TypePressure
  /** Server-rendered access window, shared with enforcement and patrol. */
  accessHours?: string
  tenantId: string
  active: boolean
  onToggle: () => void
  onViewParkers?: () => void
}) {
  const full = used >= cap
  // Clamped: an over-cap bucket (an operator lowered the cap under existing
  // permits) must not render a bar wider than its track.
  const pct = cap > 0 ? Math.min(100, Math.round((used / cap) * 100)) : 0
  const queue = pendingLabel(pressure)
  const [copied, setCopied] = useState(false)
  // Built from the CURRENT origin so the link a manager copies points at the
  // host they're actually signed in to, rather than a baked-in domain that goes
  // stale the moment the portal moves.
  const link = `${window.location.origin}/?r=${encodeURIComponent(tenantId)}&type=${encodeURIComponent(type)}`

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link)
      setCopied(true)
      setTimeout(() => setCopied(false), 1800)
    } catch {
      // Clipboard can be denied (permissions, insecure context). Say nothing
      // false — the link is on screen and selectable either way.
      setCopied(false)
    }
  }

  return (
    <div style={{
      background: '#fff', borderRadius: 12, padding: '12px 14px',
      border: `1.5px solid ${active ? '#7F3F98' : 'var(--color-gray-200)'}`,
      width: '100%', minWidth: 0,
    }}>
      <button
        onClick={onToggle}
        aria-pressed={active}
        style={{
          display: 'block', width: '100%', textAlign: 'left', background: 'transparent',
          border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'inherit',
        }}
      >
        <div style={{ fontSize: 14, fontWeight: 700 }}>{typeLabel(type)}</div>
        <div style={{ fontSize: 12, fontWeight: 600, marginTop: 2, color: full ? '#b91c1c' : 'var(--color-gray-600)' }}>
          {capacityLabel(used, cap)}
        </div>
        <div style={{ height: 6, borderRadius: 999, background: 'var(--color-gray-100)', marginTop: 8, overflow: 'hidden' }}>
          <div style={{ width: `${pct}%`, height: '100%', background: full ? '#b91c1c' : '#7F3F98' }} />
        </div>
        {/* Pending requests count against this same ceiling, so they belong on
            the card that states it. Without this the bar reads "room to spare"
            while a queue no one can fit sits one tab away.

            The row is ALWAYS rendered, with a reserved `minHeight`, even when
            there is no queue to report — otherwise a card with an empty queue is
            one line shorter and its sign-up block sits above its neighbours',
            which reads as a layout bug rather than as "nothing is waiting". The
            demo reserves the same space for the same reason. */}
        <div style={{
          fontSize: 11.5, marginTop: 8, fontWeight: 700, minHeight: 16,
          color: pressure && pressure.short > 0 ? '#b91c1c' : '#92400e',
        }}>
          {queue ?? ''}
        </div>
        {accessHours && (
          <div style={{ marginTop: 10 }}>
            <div style={{ ...labelStyle, marginBottom: 3 }}>Access window</div>
            <div style={{ fontSize: 12, fontWeight: 700 }}>{accessHours}</div>
            <div style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--color-gray-600)', marginTop: 2 }}>
              Outside this window the plate isn't authorized and can be cited.
            </div>
          </div>
        )}
      </button>
      {/* The sign-up link, per TYPE — which is what lets a request arrive
          already knowing which permit it is for, so approving is "confirm this
          person works here" rather than "pick a permit". Readable rather than
          secret on purpose: approval is the safety net, which is exactly why
          approval exists. */}
      <div style={{ marginTop: 11, paddingTop: 10, borderTop: '1px solid var(--color-gray-100)' }}>
        <div style={{ ...labelStyle, marginBottom: 5 }}>Sign-up link</div>
        <code style={{
          display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          fontFamily: 'ui-monospace, monospace', fontSize: 11, fontWeight: 600,
          color: 'var(--color-gray-600)', background: 'var(--color-gray-100)',
          borderRadius: 8, padding: '6px 9px', marginBottom: 8,
        }}>{link}</code>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <button onClick={() => void copy()} style={{ ...ghostBtn, padding: '6px 12px', fontSize: 12 }}>
            {copied ? 'Copied' : 'Copy link'}
          </button>
          {onViewParkers && (
            <button
              onClick={onViewParkers}
              style={{
                marginLeft: 'auto', background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                fontFamily: 'inherit', fontSize: 12, fontWeight: 700, color: '#7F3F98',
              }}
            >
              View parkers →
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export interface PermitProgram {
  type: string
  /** Named permits only: this card IS one permit, shown by its own name. */
  permitId?: string
  name?: string
  permits: RosterPermit[]
  parkerCount: number
  plateCount: number
  status: RosterPermit['status']
  commonWindow: string | null
  accessHours: string | null
}

/**
 * A permit record is one parker's enforcement grant, not a permit PROGRAM.
 * Grouping by the closed permit-type key keeps six employees under the one
 * "Employee Permit" a tenant is allowed to add to, while preserving every raw
 * record for the Parkers roster and enforcement.
 */
export function groupPermitPrograms(permits: RosterPermit[], opts: { byName?: boolean } = {}): PermitProgram[] {
  // Named permits (the operator's setting for a tenant that manages a fixed set
  // of lists, Canvas Properties at Otto's): each permit is its own card, so two
  // lists of the same type are never merged into one "Employee Permit".
  if (opts.byName) {
    return permits
      .map<PermitProgram>(p => ({
        ...programOf(p.permitType || DEFAULT_PERMIT_TYPE, [p]),
        permitId: p.id,
        name: p.label,
      }))
      .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
  }
  const groups = new Map<string, RosterPermit[]>()
  for (const permit of permits) {
    const type = permit.permitType || DEFAULT_PERMIT_TYPE
    groups.set(type, [...(groups.get(type) ?? []), permit])
  }
  return [...groups.entries()].map<PermitProgram>(([type, rows]) => programOf(type, rows))
    .sort((a, b) => b.parkerCount - a.parkerCount || a.type.localeCompare(b.type))
}

function programOf(type: string, rows: RosterPermit[]): PermitProgram {
  const windows = new Set(rows.map(windowLabel))
  const schedules = new Set(rows.map(row => row.accessHours ?? ''))
  const statuses = new Set(rows.map(row => row.status))
  return {
    type,
    permits: rows,
    // People, not records: one pooled permit holds many people.
    parkerCount: countRosterParkers(rows),
    plateCount: rows.reduce((sum, row) => sum + row.vehicles.length, 0),
    status: statuses.has('expiringSoon') ? 'expiringSoon' : statuses.has('active') ? 'active' : 'expired',
    commonWindow: windows.size === 1 ? [...windows][0]! : null,
    accessHours: schedules.size === 1 && !schedules.has('') ? [...schedules][0]! : null,
  }
}

/** The sign-up link for a shared program: tenant AND program, so the request lands in this allocation. */
export function programRegistrationLink(origin: string, tenantId: string, programId: string, opts: { reserved?: boolean } = {}): string {
  return `${origin}/?r=${encodeURIComponent(tenantId)}&program=${encodeURIComponent(programId)}${opts.reserved ? '&reserved=1' : ''}`
}

/** Seat sentence for a fixed allocation. Held includes pending approvals still finishing; issued is parkers who can park now. */
export function allocationSummary(program: Pick<ProgramAllocation, 'seats' | 'held' | 'issued' | 'paused'>): string {
  const open = Math.max(0, program.seats - program.held)
  const base = `${program.held} of ${program.seats} ${program.seats === 1 ? 'seat' : 'seats'} used · ${program.issued} ${program.issued === 1 ? 'parker' : 'parkers'} issued`
  if (program.paused) return `${base} · paused by your operator, no new approvals`
  return `${base} · ${open} ${open === 1 ? 'seat' : 'seats'} open`
}

function ProgramAllocationCard({ program, tenantId }: { program: ProgramAllocation; tenantId: string }) {
  const [copied, setCopied] = useState<'general' | 'reserved' | null>(null)
  const link = programRegistrationLink(window.location.origin, tenantId, program.id)
  const reservedLink = programRegistrationLink(window.location.origin, tenantId, program.id, { reserved: true })
  const freeBays = (program.bayPool ?? []).filter(b => !b.issuanceId && b.state === 'active').length
  const copy = async (which: 'general' | 'reserved') => {
    try {
      await navigator.clipboard.writeText(which === 'reserved' ? reservedLink : link)
      setCopied(which)
      setTimeout(() => setCopied(null), 1800)
    } catch { setCopied(null) }
  }
  return (
    <div aria-label={`Shared program ${program.name}`} style={{ background: '#fff', border: '1px solid var(--color-gray-200)', borderRadius: 12, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
      <div style={{ minWidth: 200, flex: '1 1 240px' }}>
        <div style={{ fontSize: 15, fontWeight: 800 }}>{program.name} <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--color-gray-600)' }}>· shared program</span></div>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-gray-600)', marginTop: 3 }}>{allocationSummary(program)}</div>
        {(program.bayPool?.length ?? 0) > 0 && (
          <div style={{ fontSize: 12, fontWeight: 600, color: '#7F3F98', marginTop: 3 }}>
            {program.bayPool!.length} of {program.seats} {program.seats === 1 ? 'seat is a numbered bay' : 'seats are numbered bays'}
            {' · '}{program.bayPool!.filter(b => b.issuanceId).length} in use{program.bayPool!.some(b => b.state === 'out_of_service') ? ` · ${program.bayPool!.filter(b => b.state === 'out_of_service').length} out of service` : ''}
          </div>
        )}
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-gray-600)', marginTop: 3 }}>
          Staff who register through this link are approved into your allocation. Seats you do not use are not shared with other businesses.
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'stretch' }}>
        <button onClick={() => void copy('general')} disabled={program.paused} style={{ ...ghostBtn, whiteSpace: 'nowrap' }}>
          {copied === 'general' ? 'Link copied' : 'Copy program sign-up link'}
        </button>
        {/* A second link only when there is a bay to give: it tells the applicant
            the sign-up is for a numbered bay, and tags the request so the approver
            picks one. Hidden when the pool is full or absent — a reserved link with
            nothing to reserve is a promise the manager cannot keep. */}
        {freeBays > 0 && (
          <button onClick={() => void copy('reserved')} disabled={program.paused} style={{ ...ghostBtn, whiteSpace: 'nowrap' }} title={`${freeBays} reserved ${freeBays === 1 ? 'bay is' : 'bays are'} free in your pool`}>
            {copied === 'reserved' ? 'Link copied' : 'Copy reserved-bay sign-up link'}
          </button>
        )}
      </div>
    </div>
  )
}

/** One tenant-visible permit program. Detailed people remain on Parkers. */
function PermitProgramCard({ program, tenantId, onViewParkers }: {
  program: PermitProgram
  tenantId: string
  onViewParkers?: () => void
}) {
  const tone = STATUS_TONE[program.status]
  const [copied, setCopied] = useState(false)
  const link = program.permitId
    ? `${window.location.origin}/?r=${encodeURIComponent(tenantId)}&permit=${encodeURIComponent(program.permitId)}`
    : `${window.location.origin}/?r=${encodeURIComponent(tenantId)}&type=${encodeURIComponent(program.type)}`
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link)
      setCopied(true)
      setTimeout(() => setCopied(false), 1800)
    } catch { setCopied(false) }
  }

  return (
    <div style={{ background: '#fff', border: '1px solid var(--color-gray-200)', borderRadius: 12, overflow: 'hidden' }}>
      <div style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 200, flex: '1 1 240px' }}>
          <div style={{ fontSize: 15, fontWeight: 800 }}>{program.name ?? `${typeLabel(program.type)} Permit`}</div>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-gray-600)', marginTop: 2 }}>
            {program.parkerCount} {program.parkerCount === 1 ? 'parker' : 'parkers'} · {program.plateCount} {program.plateCount === 1 ? 'plate' : 'plates'}
          </div>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-gray-600)', marginTop: 3 }}>
            {program.commonWindow ?? 'Access dates are managed for each parker.'}
          </div>
          {program.accessHours && (
            <div style={{ fontSize: 12.5, fontWeight: 700, color: '#7F3F98', marginTop: 4 }}>
              {program.accessHours}
            </div>
          )}
        </div>
        <span style={{
          fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.5px',
          padding: '4px 10px', borderRadius: 999,
          background: tone.bg, color: tone.fg, border: `1px solid ${tone.border}`,
        }}>{tone.label}</span>
        {onViewParkers && (
          <button
            onClick={onViewParkers}
            style={{ marginLeft: 'auto', background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, fontWeight: 700, color: '#7F3F98' }}
          >
            View parkers →
          </button>
        )}
      </div>
      <div style={{ padding: '11px 16px 14px', borderTop: '1px solid var(--color-gray-100)', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 260px', minWidth: 0 }}>
          <div style={{ ...labelStyle, marginBottom: 4 }}>Add staff to this permit</div>
          <code style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'ui-monospace, monospace', fontSize: 11, fontWeight: 600, color: 'var(--color-gray-600)' }}>{link}</code>
        </div>
        <button onClick={() => void copy()} style={{ ...ghostBtn, padding: '6px 12px', fontSize: 12 }}>
          {copied ? 'Copied' : 'Copy sign-up link'}
        </button>
      </div>
    </div>
  )
}
